import React, { useState, useEffect, useMemo } from 'react';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import type { SatGroup, Satellite } from '../../../../types/mission';
import { showToastMessage } from '../../../../../../../utils/commonFunctions';
import styles from './SensorConfigPanel.module.scss';

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CaretIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 120ms linear', flexShrink: 0 }}
  >
    <rect width="16" height="15.3333" fill="black" fillOpacity="0.01" />
    <path d="M8.86236 9.63642C8.56947 9.92931 8.09459 9.92931 7.8017 9.63642L4.61236 6.44708C4.13989 5.9746 4.47451 5.16675 5.14269 5.16675L11.5214 5.16675C12.1895 5.16675 12.5242 5.9746 12.0517 6.44708L8.86236 9.63642Z" fill="currentColor" />
  </svg>
);

// Crisp Satellite Icon identical to Satellite creation tree
const SatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
    <path d="M7.38489 2.0577L5.67255 0.345363C5.61172 0.28454 5.5127 0.284256 5.45563 0.348614C4.98336 0.881179 4.77115 1.56372 4.81869 2.2321M4.81869 2.2321C4.8612 2.82971 5.11137 3.41599 5.56899 3.8736C6.00192 4.30654 6.64661 4.56249 7.2105 4.6239C7.90903 4.69997 8.54737 4.48325 9.09456 3.9876C9.15832 3.92854 9.15805 3.83087 9.09723 3.77005L7.38489 2.0577M7.38489 2.0577L8.31883 1.12376M4.81869 2.2321L3.78222 3.26857M7.2105 4.6239L6.17403 5.66037M3.78222 3.26857L2.32021 1.80655C2.25938 1.74572 2.16077 1.74572 2.09995 1.80655L0.346182 3.56031C0.285359 3.62114 0.285359 3.71975 0.346182 3.78057L1.8082 5.24259M3.78222 3.26857L1.8082 5.24259M1.8082 5.24259L1.39323 5.65757C0.987741 6.06305 0.98774 6.72047 1.39322 7.12595L2.31664 8.04937C2.72212 8.45485 3.37955 8.45485 3.78503 8.04937L4.20001 7.63439M6.17403 5.66037L4.20001 7.63439M6.17403 5.66037L7.63605 7.12239C7.69687 7.18321 7.69687 7.28183 7.63605 7.34265L5.88228 9.09641C5.82146 9.15724 5.72285 9.15724 5.66202 9.09641L4.20001 7.63439" stroke="#eee" strokeWidth="0.65" strokeLinecap="round" />
  </svg>
);

const SatGroupIcon = () => (
  <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
    <path d="M5.43499 2.06419C5.56386 2.26322 5.78786 2.39493 6.04262 2.39493C6.29922 2.39493 6.52461 2.26132 6.65302 2.05987M5.43499 2.06419C5.36178 1.95111 5.31928 1.81631 5.31928 1.67159C5.31928 1.27209 5.64313 0.948242 6.04262 0.948242C6.44211 0.948242 6.76596 1.27209 6.76596 1.67159C6.76596 1.81448 6.72453 1.94769 6.65302 2.05987M5.43499 2.06419C4.89832 2.78912 4.52344 4.44043 4.52344 6.36151C4.52344 8.95168 5.20494 11.0514 6.04561 11.0514C6.50863 11.0514 6.92336 10.4145 7.20254 9.40942M6.65302 2.05987C6.8691 2.34992 7.05908 2.78999 7.2091 3.33738M7.4908 4.88489C7.53727 5.31689 7.56391 5.77675 7.56738 6.25421" stroke="#eee" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.28606 5.0432C7.52922 3.74812 9.68841 3.28844 10.1087 4.01648C10.5174 4.7243 9.13202 6.29418 6.99337 7.57098M3.16738 6.55924C2.60932 7.0648 2.21065 7.55418 2.02727 7.96535M4.6826 8.67334C3.84319 8.9691 3.11336 9.09885 2.61378 9.03688M2.61378 9.03688C2.67112 8.93324 2.70376 8.81403 2.70376 8.6872C2.70376 8.30344 2.40493 7.98949 2.02727 7.96535C2.01178 7.96436 1.99616 7.96385 1.98042 7.96385C1.58093 7.96385 1.25708 8.28771 1.25708 8.6872C1.25708 9.08669 1.58093 9.41054 1.98042 9.41054C2.25308 9.41054 2.4905 9.25968 2.61378 9.03688Z" stroke="#eee" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.7308 4.06637C3.35919 3.57519 2.27123 3.52167 1.98569 4.01624C1.56536 4.74428 3.04305 6.38435 5.2862 7.67943C6.95118 8.64071 8.56989 9.14173 9.46562 9.03843M10.0753 7.98356C9.69111 8.00082 9.38495 8.31774 9.38495 8.70616C9.38495 8.82595 9.41407 8.93894 9.46562 9.03843M10.0753 7.98356C10.0862 7.98307 10.0972 7.98282 10.1083 7.98282C10.5078 7.98282 10.8316 8.30667 10.8316 8.70616C10.8316 9.10565 10.5078 9.4295 10.1083 9.4295C9.82859 9.4295 9.58597 9.27075 9.46562 9.03843M10.0753 7.98356C9.93084 7.64979 9.64234 7.26326 9.24018 6.85774" stroke="#eee" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface DisplaySensor {
  id: string;
  name: string;
  type: 'MSI' | 'SAR';
}

interface DisplaySat {
  id: string;
  name: string;
  sensors: DisplaySensor[];
}

interface DisplayGroup {
  id: string;
  name: string;
  satellites: DisplaySat[];
}

interface SensorConfigPanelProps {
  parentPos?: { top: number; left: number };
  selectedSources?: { id: string; name: string; kind?: string; objectType?: string }[];
}

const SensorConfigPanel: React.FC<SensorConfigPanelProps> = ({ parentPos, selectedSources = [] }) => {
  const {
    sensorConfigOpen,
    closeSensorConfig,
    satellites,
    leftPanelCollapsed,
    coverageAnalyserOpen,
    selectedCoverageSensors,
    setSelectedCoverageSensors,
  } = useMDCockpit();

  const defaultLeft = leftPanelCollapsed ? 340 : 570;
  const panelTop = parentPos ? parentPos.top : 60;
  const panelLeft = parentPos ? parentPos.left + 332 : defaultLeft;

  const selSourceIds = useMemo(() => new Set(selectedSources.map(s => s.id)), [selectedSources]);

  // Convert dynamic cockpit satellites state to tree display, filtering ONLY selected sources
  const treeData = useMemo<DisplayGroup[]>(() => {
    if (!satellites || satellites.length === 0 || selSourceIds.size === 0) return [];

    const out: DisplayGroup[] = [];
    const standaloneSats: DisplaySat[] = [];

    for (const item of satellites) {
      if ((item as SatGroup).type === 'group') {
        const grp = item as SatGroup;
        const isGroupSelected = selSourceIds.has(grp.id);

        const includedSats: DisplaySat[] = (grp.children || [])
          .filter(child => isGroupSelected || selSourceIds.has(child.id))
          .map(child => {
            const rawSensors = child.sensors || [];
            const displaySensors: DisplaySensor[] = rawSensors.length > 0
              ? rawSensors.map((s, idx) => ({
                  id: (s as any).id || s.sensorId || `${child.id}-sen-${idx + 1}`,
                  name: s.name?.trim() || s.sensorId?.trim() || `Sensor_0${idx + 1}`,
                  type: (s as any).sensorType === 'SAR' || (s.geometry as string) === 'rectangular' ? 'SAR' : 'MSI',
                }))
              : [];

            return {
              id: child.id,
              name: child.name,
              sensors: displaySensors,
            };
          });

        if (includedSats.length > 0) {
          out.push({
            id: grp.id,
            name: grp.name,
            satellites: includedSats,
          });
        }
      } else {
        const sat = item as Satellite;
        if (selSourceIds.has(sat.id)) {
          const rawSensors = sat.sensors || [];
          const displaySensors: DisplaySensor[] = rawSensors.length > 0
            ? rawSensors.map((s, idx) => ({
                id: (s as any).id || s.sensorId || `${sat.id}-sen-${idx + 1}`,
                name: s.name?.trim() || s.sensorId?.trim() || `Sensor_0${idx + 1}`,
                type: (s as any).sensorType === 'SAR' || (s.geometry as string) === 'rectangular' ? 'SAR' : 'MSI',
              }))
            : [];

          standaloneSats.push({
            id: sat.id,
            name: sat.name,
            sensors: displaySensors,
          });
        }
      }
    }

    if (standaloneSats.length > 0) {
      out.push({
        id: 'grp-standalone',
        name: 'Satellites',
        satellites: standaloneSats,
      });
    }

    return out;
  }, [satellites, selSourceIds]);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSats, setExpandedSats] = useState<Set<string>>(new Set());
  const isInitializedRef = React.useRef(false);
  const [draftSensors, setDraftSensors] = useState<Record<string, string>>({});

  // Dynamically initialize expanded groups/sats, local draft, and default 1st sensor per satellite if unset
  useEffect(() => {
    if (!sensorConfigOpen) {
      isInitializedRef.current = false;
      return;
    }
    if (!isInitializedRef.current && treeData.length > 0) {
      isInitializedRef.current = true;
      setExpandedGroups(new Set(treeData.map(g => g.id)));
      setExpandedSats(new Set(treeData.flatMap(g => g.satellites.map(s => s.id))));

      let currentDraft = { ...selectedCoverageSensors };
      // Ensure each satellite with sensors has at least 1 sensor selected by default if unset
      treeData.flatMap(g => g.satellites).forEach(sat => {
        if (!currentDraft[sat.id] && sat.sensors.length > 0) {
          currentDraft[sat.id] = sat.sensors[0].id;
        }
      });
      setDraftSensors(currentDraft);
    }
  }, [sensorConfigOpen, treeData, selectedCoverageSensors]);

  // Auto-close Sensor Config if parent Coverage Analysis closes
  if (!sensorConfigOpen || !coverageAnalyserOpen) return null;

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSat = (id: string) => {
    setExpandedSats(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectSensor = (satId: string, sensorId: string) => {
    setDraftSensors(prev => ({
      ...prev,
      [satId]: sensorId,
    }));
  };

  const handleSave = () => {
    setSelectedCoverageSensors(draftSensors);
    showToastMessage('success', 'Sensors configured');
    closeSensorConfig();
  };

  return (
    <div
      className={styles.panel}
      style={{
        position: 'absolute',
        top: panelTop,
        left: panelLeft,
        zIndex: 40,
        width: 320,
        transition: parentPos ? 'none' : 'left 220ms ease',
      }}
    >
      {/* Header — non-draggable, locks position to parent Coverage Analysis */}
      <div className={styles.header} style={{ cursor: 'default', userSelect: 'none' }}>
        <span className={styles.headerTitle}>Sensor Config</span>
        <button className={styles.headerBtn} type="button" title="Close" onClick={closeSensorConfig}>
          <CloseIcon />
        </button>
      </div>

      <div className={styles.body}>
        {/* Tree View (Matching Satellite Creation Left Panel) */}
        <div className={styles.treeContainer}>
          {treeData.length === 0 ? (
            <div style={{ padding: '12px 4px', fontSize: 12, color: 'rgba(238, 238, 238, 0.4)' }}>
              No satellites available in current mission.
            </div>
          ) : (
            treeData.map(grp => {
              const grpExpanded = expandedGroups.has(grp.id);
              return (
                <div key={grp.id} className={styles.treeGroup}>
                  {/* Group Node */}
                  <div className={styles.groupHeader} onClick={() => toggleGroup(grp.id)}>
                    <span className={styles.caretIcon}>
                      <CaretIcon open={grpExpanded} />
                    </span>
                    <span className={styles.constellationIcon}>
                      <SatGroupIcon />
                    </span>
                    <span className={styles.groupDot} />
                    <span className={styles.groupName}>{grp.name}</span>
                  </div>

                  {/* Satellites */}
                  {grpExpanded && (
                    <div className={styles.groupBody}>
                      {grp.satellites.map(sat => {
                        const satExpanded = expandedSats.has(sat.id);
                        const isSelected = Boolean(draftSensors[sat.id]);
                        return (
                          <div key={sat.id} className={styles.satChild}>
                            <div className={styles.satHeader} onClick={() => toggleSat(sat.id)}>
                              <span className={styles.satIcon}>
                                <SatIcon />
                              </span>
                              <span className={styles.satName}>{sat.name}</span>
                              <span className={styles.satCount}>
                                {isSelected ? '01' : '00'}
                              </span>
                            </div>

                            {/* Sensors */}
                            {satExpanded && (
                              <div className={styles.sensorBody}>
                                {sat.sensors.length === 0 ? (
                                  <div style={{ padding: '4px 8px', fontSize: 11, color: 'rgba(238, 238, 238, 0.35)' }}>
                                    No sensors configured
                                  </div>
                                ) : (
                                  sat.sensors.map(sen => {
                                    const selected = draftSensors[sat.id] === sen.id;
                                    return (
                                      <div key={sen.id} className={styles.sensorChild}>
                                        <div className={styles.sensorRow} onClick={() => handleSelectSensor(sat.id, sen.id)}>
                                          <div className={`${styles.checkboxSquare} ${selected ? styles.checked : ''}`}>
                                            {selected && <CheckIcon />}
                                          </div>
                                          <span className={styles.sensorName}>{sen.name}</span>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button className={styles.cancelBtn} type="button" onClick={closeSensorConfig}>
          Cancel
        </button>
        <button className={`${styles.runBtn} ${styles.activeRun}`} type="button" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
};

export default SensorConfigPanel;
